import React, { useEffect, useMemo, useRef, useState } from "react";
import BreadCrumb from "components/common/BreadCrumb";
import { useNavigate, useParams } from "react-router-dom";
import {
  Col,
  Row,
  Input,
  Label,
  Form,
  Card,
  CardBody,
  CardHeader,
  FormFeedback,
  Container,
} from "reactstrap";
import { FormikProvider, useFormik } from "formik";
import schema from "./schema";
import { toast } from "react-toastify";
import type {
  AddClientRequest,
  Client,
  ClientContact,
} from "features/Clients/types/client.types";
import {
  createClient,
  getClient,
  updateClient,
} from "features/Clients/services/ClientService";
import { BackButton } from "components/common/Buttons";
import { getDocumentTitle } from "utils/helpers";
import userPlaceholder from "assets/images/users/user-dummy-img.jpg";
import PageLoadingSpinner from "components/common/PageLoader";
import {
  buildClientJsonPayload,
  clientFieldsToFormData,
  type ClientContactFormRow,
  type ClientFormFields,
} from "features/Clients/utils/clientFormPayload";
import { getClientAvatarUrl } from "features/Clients/utils/clientAvatarUrl";
import {
  ClientType,
  ClientTypeLabelMap,
} from "features/Clients/constants/client-organization.constants";
import { AsyncPaginatedSelect } from "components/common/AsyncPaginatedSelect";
import { getSectorOptions } from "features/Meta/services/MetaService";
import ClientContactsFieldArray from "features/Clients/components/ClientContactsFieldArray";

interface ClientFormProps {
  isEdit?: boolean;
}

function mapContactsToFormRows(
  contacts: ClientContact[] | null | undefined,
): ClientContactFormRow[] {
  if (!contacts?.length) {
    return [
      {
        firstName: "",
        lastName: "",
        email: "",
        phone: "",
        designation: "",
        isPrimary: true,
      },
    ];
  }
  return contacts.map((c) => ({
    id: c.id,
    firstName: c.firstName ?? "",
    lastName: c.lastName ?? "",
    email: c.email ?? "",
    phone: c.phone ?? "",
    designation: c.designation ?? "",
    isPrimary: Boolean(c.isPrimary),
  }));
}

function defaultNameFromRecord(record: Client | null): string {
  if (!record) return "";
  const n = String(record.name ?? "").trim();
  if (n) return n;
  const co = String(record.company ?? "").trim();
  return co;
}

const ClientForm = ({ isEdit = false }: ClientFormProps) => {
  document.title = getDocumentTitle(isEdit ? "Edit Client" : "New Client");

  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [record, setRecord] = useState<Client | null>(null);
  const [loadingRecord, setLoadingRecord] = useState(Boolean(isEdit));
  const [avatarPreview, setAvatarPreview] = useState<string | null>(null);
  const [removeAvatar, setRemoveAvatar] = useState(false);
  const avatarInputRef = useRef<HTMLInputElement>(null);

  const initialValues = useMemo(
    () => ({
      name: defaultNameFromRecord(record),
      type:
        record?.type != null && String(record?.type).trim() !== ""
          ? String(record?.type)
          : ClientType.PRIVATE,
      sectorId:
        record?.sectorId != null && Number(record.sectorId) > 0
          ? Number(record.sectorId)
          : "",
      website: record?.website ?? "",
      gstNumber: record?.gstNumber ?? "",
      panNumber: record?.panNumber ?? "",
      billingAddress: record?.billingAddress ?? "",
      shippingAddress: record?.shippingAddress ?? "",
      company: record?.company ?? "",
      address: record?.address ?? "",
      city: record?.city ?? "",
      state: record?.state ?? "",
      email: record?.email ?? "",
      phone: record?.phone ?? "",
      contacts: mapContactsToFormRows(record?.contacts ?? null),
      image: null as File | null,
    }),
    [record],
  );

  const validation = useFormik({
    enableReinitialize: true,
    initialValues,
    validationSchema: schema,
    onSubmit: async (values) => {
      const { image, contacts, ...rest } = values;
      const avatarFile = image instanceof File ? image : null;
      const fields: ClientFormFields = {
        name: String(rest.name ?? ""),
        type: String(rest.type ?? ""),
        sectorId: rest.sectorId,
        website: String(rest.website ?? ""),
        gstNumber: String(rest.gstNumber ?? ""),
        panNumber: String(rest.panNumber ?? ""),
        billingAddress: String(rest.billingAddress ?? ""),
        shippingAddress: String(rest.shippingAddress ?? ""),
        company: String(rest.company ?? ""),
        address: String(rest.address ?? ""),
        city: String(rest.city ?? ""),
        state: String(rest.state ?? ""),
        email: String(rest.email ?? ""),
        phone: String(rest.phone ?? ""),
      };
      const contactRows = (contacts ?? []) as ClientContactFormRow[];
      const jsonPayload: AddClientRequest = buildClientJsonPayload(
        fields,
        contactRows,
      );

      try {
        let response;
        if (isEdit && id) {
          if (avatarFile) {
            response = await updateClient(
              Number(id),
              clientFieldsToFormData(fields, contactRows, avatarFile),
            );
          } else if (removeAvatar) {
            const fd = clientFieldsToFormData(fields, contactRows, null);
            fd.append("removeAvatar", "true");
            response = await updateClient(Number(id), fd);
          } else {
            response = await updateClient(Number(id), jsonPayload);
          }
        } else if (avatarFile) {
          response = await createClient(
            clientFieldsToFormData(fields, contactRows, avatarFile),
          );
        } else {
          response = await createClient(jsonPayload);
        }

        if (response.success) {
          toast.success(response.message);
          navigate("/clients");
        } else {
          toast.error(response.message);
        }
      } catch (err: unknown) {
        const msg =
          (err as { response?: { data?: { message?: string } } })?.response
            ?.data?.message ??
          (err as Error)?.message ??
          "Request failed";
        toast.error(msg);
        console.warn(err);
      }
    },
  });

  const handleImageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    const ext = ("." + (file.name.split(".").pop() ?? "")).toLowerCase();
    const allowedMime = ["image/png", "image/jpeg"];
    const allowedExt = [".png", ".jpg", ".jpeg"];
    if (!allowedMime.includes(file.type) || !allowedExt.includes(ext)) {
      toast.error("Only PNG, JPG, and JPEG images are accepted.");
      event.target.value = "";
      return;
    }
    const reader = new FileReader();
    reader.onload = (e) => {
      validation.setFieldValue("image", file);
      setAvatarPreview(e.target?.result as string);
      setRemoveAvatar(false);
    };
    reader.readAsDataURL(file);
  };

  const handleRemoveAvatar = () => {
    validation.setFieldValue("image", null);
    setAvatarPreview(null);
    setRemoveAvatar(true);
    if (avatarInputRef.current) avatarInputRef.current.value = "";
  };

  useEffect(() => {
    if (!isEdit || !id) {
      setLoadingRecord(false);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        setLoadingRecord(true);
        const res = await getClient(Number(id));
        if (cancelled) return;
        if (res.success && res.data) {
          setRecord(res.data);
          setAvatarPreview(null);
          setRemoveAvatar(false);
        } else {
          toast.error(res.message || "Client not found");
          navigate("/clients");
        }
      } catch (err) {
        if (!cancelled) {
          toast.error((err as Error)?.message || "Failed to load client");
          navigate("/clients");
        }
      } finally {
        if (!cancelled) setLoadingRecord(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isEdit, id, navigate]);

  if (loadingRecord) {
    return <PageLoadingSpinner message="Loading client…" />;
  }

  const avatarDisplaySrc = removeAvatar
    ? userPlaceholder
    : (avatarPreview ?? getClientAvatarUrl(record?.image) ?? userPlaceholder);

  return (
    <div className="page-content">
      <Container fluid>
        <BreadCrumb
          title={isEdit ? "Edit Client" : "New Client"}
          pageTitle="Customers"
          items={[{ label: "Clients", path: "/clients" }]}
        />
        <FormikProvider value={validation}>
          <Form
            className="needs-validation"
            onSubmit={(e) => {
              e.preventDefault();
              validation.handleSubmit();
              return false;
            }}
          >
            <Row className="g-4">
              <Col lg={8}>
                <Card>
                  <CardHeader>
                    <h5 className="card-title mb-0">Organization</h5>
                    <p className="text-muted small mb-0 mt-1">
                      Legal or display name, classification, and tax
                      identifiers. Legacy person fields remain optional.
                    </p>
                  </CardHeader>
                  <CardBody>
                    <Row className="g-3">
                      <Col md={12}>
                        <div className="text-center mb-4">
                          <div className="position-relative d-inline-block">
                            <div className="position-absolute top-100 start-100 translate-middle">
                              <Label
                                htmlFor="client-avatar-input"
                                className="mb-0"
                                title="Select image"
                              >
                                <div className="avatar-xs cursor-pointer">
                                  <div className="avatar-title bg-light border rounded-circle text-muted">
                                    <i className="ri-image-fill" />
                                  </div>
                                </div>
                              </Label>
                              <Input
                                innerRef={avatarInputRef}
                                className="form-control d-none"
                                id="client-avatar-input"
                                type="file"
                                accept=".jpg,.jpeg,.png"
                                onChange={handleImageChange}
                                invalid={Boolean(
                                  validation.touched.image &&
                                  validation.errors.image,
                                )}
                              />
                            </div>
                            <div className="avatar-lg">
                              <div
                                className={`avatar-title bg-light rounded overflow-hidden ${
                                  validation.touched.image &&
                                  validation.errors.image
                                    ? "border border-danger"
                                    : ""
                                }`}
                              >
                                <img
                                  src={avatarDisplaySrc}
                                  alt="Client"
                                  className="avatar-md h-auto"
                                  style={{ objectFit: "cover" }}
                                />
                              </div>
                            </div>
                          </div>
                          {validation.errors.image &&
                          validation.touched.image ? (
                            <FormFeedback
                              type="invalid"
                              className="d-block mt-3"
                            >
                              {String(validation.errors.image)}
                            </FormFeedback>
                          ) : null}
                          {(avatarPreview || record?.image) && !removeAvatar ? (
                            <button
                              type="button"
                              className="btn btn-link btn-sm text-danger p-0 mt-2"
                              onClick={handleRemoveAvatar}
                            >
                              <i className="ri-delete-bin-line me-1" />
                              Remove photo
                            </button>
                          ) : null}
                          <small className="form-text text-muted d-block mt-2">
                            PNG, JPG, or JPEG only.
                          </small>
                        </div>
                      </Col>

                      <Col md={8}>
                        <Label className="form-label" for="client-name">
                          Name <span className="text-danger">*</span>
                        </Label>
                        <Input
                          id="client-name"
                          name="name"
                          value={validation.values.name}
                          onChange={validation.handleChange}
                          onBlur={validation.handleBlur}
                          invalid={Boolean(
                            validation.touched.name && validation.errors.name,
                          )}
                        />
                        {validation.touched.name && validation.errors.name ? (
                          <FormFeedback type="invalid">
                            {String(validation.errors.name)}
                          </FormFeedback>
                        ) : null}
                      </Col>
                      <Col md={4}>
                        <Label className="form-label" for="client-type">
                          Type
                        </Label>
                        <Input
                          id="client-type"
                          type="select"
                          name="type"
                          value={validation.values.type}
                          onChange={validation.handleChange}
                          onBlur={validation.handleBlur}
                          invalid={Boolean(
                            validation.touched.type && validation.errors.type,
                          )}
                        >
                          {(Object.values(ClientType) as ClientType[]).map(
                            (t) => (
                              <option key={t} value={t}>
                                {ClientTypeLabelMap[t]}
                              </option>
                            ),
                          )}
                        </Input>
                        {validation.touched.type && validation.errors.type ? (
                          <FormFeedback type="invalid">
                            {String(validation.errors.type)}
                          </FormFeedback>
                        ) : null}
                      </Col>

                      <Col md={6}>
                        <Label className="form-label">Sector</Label>
                        <AsyncPaginatedSelect
                          fetchOptions={getSectorOptions}
                          placeholder="Search sector…"
                          defaultOption={
                            record?.sector
                              ? { value: record.sector.id, label: record.sector.name }
                              : undefined
                          }
                          value={validation.values.sectorId ?? null}
                          onChange={(opt: any) =>
                            validation.setFieldValue("sectorId", opt?.value ?? null)
                          }
                          onBlur={() => validation.setFieldTouched("sectorId", true)}
                          isInvalid={Boolean(
                            validation.touched.sectorId &&
                            validation.errors.sectorId,
                          )}
                        />
                        {validation.touched.sectorId &&
                        validation.errors.sectorId ? (
                          <div className="text-danger small mt-1">
                            {String(validation.errors.sectorId)}
                          </div>
                        ) : null}
                      </Col>

                      <Col md={6}>
                        <Label className="form-label" for="client-website">
                          Website
                        </Label>
                        <Input
                          id="client-website"
                          name="website"
                          placeholder="https://"
                          value={validation.values.website}
                          onChange={validation.handleChange}
                          onBlur={validation.handleBlur}
                          invalid={Boolean(
                            validation.touched.website && validation.errors.website,
                          )}
                        />
                        {validation.touched.website && validation.errors.website ? (
                          <FormFeedback type="invalid">
                            {String(validation.errors.website)}
                          </FormFeedback>
                        ) : null}
                      </Col>

                      <Col md={6}>
                        <Label className="form-label" for="client-gst">
                          GST number
                        </Label>
                        <Input
                          id="client-gst"
                          name="gstNumber"
                          value={validation.values.gstNumber}
                          onChange={validation.handleChange}
                          onBlur={validation.handleBlur}
                          invalid={Boolean(
                            validation.touched.gstNumber && validation.errors.gstNumber,
                          )}
                        />
                        {validation.touched.gstNumber && validation.errors.gstNumber ? (
                          <FormFeedback type="invalid">
                            {String(validation.errors.gstNumber)}
                          </FormFeedback>
                        ) : null}
                      </Col>
                      <Col md={6}>
                        <Label className="form-label" for="client-pan">
                          PAN number
                        </Label>
                        <Input
                          id="client-pan"
                          name="panNumber"
                          value={validation.values.panNumber}
                          onChange={validation.handleChange}
                          onBlur={validation.handleBlur}
                        />
                      </Col>

                      <Col md={6}>
                        <Label className="form-label" for="client-billing">
                          Billing address
                        </Label>
                        <Input
                          id="client-billing"
                          type="textarea"
                          rows={2}
                          name="billingAddress"
                          value={validation.values.billingAddress}
                          onChange={validation.handleChange}
                          onBlur={validation.handleBlur}
                        />
                      </Col>
                      <Col md={6}>
                        <Label className="form-label" for="client-shipping">
                          Shipping address
                        </Label>
                        <Input
                          id="client-shipping"
                          type="textarea"
                          rows={2}
                          name="shippingAddress"
                          value={validation.values.shippingAddress}
                          onChange={validation.handleChange}
                          onBlur={validation.handleBlur}
                        />
                      </Col>
                    </Row>
                  </CardBody>
                </Card>

                <ClientContactsFieldArray validation={validation} />
              </Col>

              <Col lg={4}>
                <Card>
                  <CardHeader>
                    <div className="d-flex align-items-center gap-2">
                      <span className="avatar-sm flex-shrink-0 bg-primary-subtle text-primary rounded-circle d-flex align-items-center justify-content-center">
                        <i className="ri-mail-send-line fs-18" />
                      </span>
                      <div>
                        <h5 className="card-title mb-0">
                          Account &amp; location
                        </h5>
                        <p className="text-muted small mb-0 mt-1">
                          Primary email and phone on file, plus legacy address
                          fields.
                        </p>
                      </div>
                    </div>
                  </CardHeader>
                  <CardBody>
                    <div className="mb-3">
                      <Label className="form-label fw-medium">Email</Label>
                      <Input
                        type="email"
                        className="form-control"
                        name="email"
                        value={validation.values.email}
                        onChange={validation.handleChange}
                        onBlur={validation.handleBlur}
                        invalid={Boolean(
                          validation.touched.email && validation.errors.email,
                        )}
                      />
                      {validation.touched.email && validation.errors.email ? (
                        <FormFeedback type="invalid">
                          {String(validation.errors.email)}
                        </FormFeedback>
                      ) : null}
                    </div>
                    <div className="mb-3">
                      <Label className="form-label fw-medium">Phone</Label>
                      <Input
                        className="form-control"
                        name="phone"
                        value={validation.values.phone}
                        onChange={validation.handleChange}
                        onBlur={validation.handleBlur}
                        invalid={Boolean(
                          validation.touched.phone && validation.errors.phone,
                        )}
                      />
                      {validation.touched.phone && validation.errors.phone ? (
                        <FormFeedback type="invalid">
                          {String(validation.errors.phone)}
                        </FormFeedback>
                      ) : null}
                    </div>
                    <div className="mb-3">
                      <Label className="form-label fw-medium">
                        Company / trade name{" "}
                        <span className="text-muted fw-normal">(optional)</span>
                      </Label>
                      <Input
                        className="form-control"
                        name="company"
                        placeholder="If different from legal name"
                        value={validation.values.company}
                        onChange={validation.handleChange}
                        onBlur={validation.handleBlur}
                      />
                    </div>
                    <div className="mb-3">
                      <Label className="form-label fw-medium">Address</Label>
                      <Input
                        type="textarea"
                        rows={3}
                        className="form-control"
                        name="address"
                        placeholder="Street, building, area"
                        value={validation.values.address}
                        onChange={validation.handleChange}
                        onBlur={validation.handleBlur}
                        invalid={Boolean(
                          validation.touched.address &&
                          validation.errors.address,
                        )}
                      />
                      {validation.touched.address &&
                      validation.errors.address ? (
                        <FormFeedback type="invalid">
                          {String(validation.errors.address)}
                        </FormFeedback>
                      ) : null}
                    </div>
                    <div className="mb-3">
                      <Label className="form-label fw-medium">City</Label>
                      <Input
                        className="form-control"
                        name="city"
                        value={validation.values.city}
                        onChange={validation.handleChange}
                        onBlur={validation.handleBlur}
                        invalid={Boolean(
                          validation.touched.city && validation.errors.city,
                        )}
                      />
                      {validation.touched.city && validation.errors.city ? (
                        <FormFeedback type="invalid">
                          {String(validation.errors.city)}
                        </FormFeedback>
                      ) : null}
                    </div>
                    <div className="mb-0">
                      <Label className="form-label fw-medium">State</Label>
                      <Input
                        className="form-control"
                        name="state"
                        value={validation.values.state}
                        onChange={validation.handleChange}
                        onBlur={validation.handleBlur}
                        invalid={Boolean(
                          validation.touched.state && validation.errors.state,
                        )}
                      />
                      {validation.touched.state && validation.errors.state ? (
                        <FormFeedback type="invalid">
                          {String(validation.errors.state)}
                        </FormFeedback>
                      ) : null}
                    </div>
                  </CardBody>
                </Card>
              </Col>

              <Col xs={12}>
                <div className="d-flex flex-wrap justify-content-end gap-3 mb-4">
                  <BackButton />
                  <button
                    type="submit"
                    className="btn btn-primary w-sm"
                    disabled={validation.isSubmitting}
                  >
                    {validation.isSubmitting ? (
                      <>
                        <span
                          className="spinner-border spinner-border-sm me-1"
                          role="status"
                          aria-hidden="true"
                        />
                        {isEdit ? "Updating…" : "Submitting…"}
                      </>
                    ) : isEdit ? (
                      "Update"
                    ) : (
                      "Submit"
                    )}
                  </button>
                </div>
              </Col>
            </Row>
          </Form>
        </FormikProvider>
      </Container>
    </div>
  );
};

export default ClientForm;
